import Image from 'next/image';
import React, { useEffect, useRef, useState } from 'react';

import { PopupFailureProps, PopupLoaderType } from '../types';
import { PopupLoaderErrorsMapping, POPUP_TIMEOUT_LIMIT } from '../constants';
import Modal from '@/lib/Modal';
import SecuredByRazorpay from '@/lib/SecuredByRazorpay';
import createPopup, { CustomPopupWindow } from '@/utils/popup';

interface OpenPopUpLoaderProps {
  url: string;
  serviceType: PopupLoaderType;
  serviceName: string;
  progressText: string;
  secondaryText?: string;
  onFail: (err: PopupFailureProps) => unknown;
  onSuccess: (data: Record<string, any>) => unknown;
}

const OpenPopupLoader: React.FC<OpenPopUpLoaderProps> = ({
  url,
  serviceName,
  serviceType,
  progressText,
  secondaryText,
  onSuccess,
  onFail,
}) => {
  const [isPopUpOpen, setIsPopUpOpen] = useState(false);
  const popUpRef = useRef<CustomPopupWindow | null>(null);
  const popUpCloseCheckTimer = useRef<NodeJS.Timeout | null>(null);
  const popupTimeout = useRef<NodeJS.Timeout | null>(null);

  // Clear the timeout set for timeout errors on success/failure
  const clearPopupTimeout = (): void => {
    if (popupTimeout.current) {
      clearTimeout(popupTimeout.current);
    }
  };

  const handlePopUpMessage = (data: string): void => {
    try {
      const parsedData = JSON.parse(data) as Record<string, any>;
      if (parsedData.error || parsedData.message) {
        onFail({
          message: parsedData.message || parsedData.error || 'Due to a technical error',
        });
      } else {
        if (popUpCloseCheckTimer.current) {
          clearInterval(popUpCloseCheckTimer.current);
          popUpCloseCheckTimer.current = null;
        }
        clearPopupTimeout();
        onSuccess(parsedData);
      }
    } catch (e: unknown) {
      clearPopupTimeout();
      onFail({
        message: 'Due to a technical error',
      });
    }
  };

  const handlePopupTimeout = (): void => {
    popupTimeout.current = setTimeout(() => {
      popUpRef.current?.close();
      onFail({
        message: PopupLoaderErrorsMapping[serviceType].timeout,
      });
    }, POPUP_TIMEOUT_LIMIT[serviceType]);
  };

  /**
   *
   * Open the popup and set the interval to check if the popup is closed
   */
  const openPopUp = (): void => {
    popUpRef.current = createPopup({
      src: url,
      name: serviceName,
      onMessageCallback: handlePopUpMessage,
    });
    setIsPopUpOpen(true);

    popUpCloseCheckTimer.current = setInterval(() => {
      if (popUpRef.current?.closed) {
        onFail({
          message: PopupLoaderErrorsMapping[serviceType].close,
        });
      }
    }, 1000);

    if (POPUP_TIMEOUT_LIMIT[serviceType]) {
      handlePopupTimeout();
    }
  };

  const focusPopUp = (): void => {
    popUpRef.current?.focus();
  };

  useEffect(() => {
    openPopUp();

    return (): void => {
      popUpRef.current?.close();

      if (popUpCloseCheckTimer.current) {
        clearInterval(popUpCloseCheckTimer.current);
      }

      // clear popup timeout
      clearPopupTimeout();
    };
  }, []);

  return (
    <Modal showClose={false} isOpen={true} onDismiss={(): null => null}>
      <Modal.Body>
        <div>
          <div className="flex flex-col items-center">
            <p className="text-center text-base text-on-grey-subtle">{progressText}</p>
            <h2 className="font-tasa font-semibold text-xl block text-on-grey-normal mt-1">
              {serviceName}
            </h2>
            <Image
              className="animate-spin mt-12 mb-12"
              src="/spinner.svg"
              alt="spinner"
              width={41}
              height={41}
            />

            <p className="text-on-grey-subtle text-sm">
              {secondaryText ?? 'This might take a few minutes'}
            </p>
            {isPopUpOpen ? (
              <button
                onClick={focusPopUp}
                className="font-tasa font-semibold my-6 text-primary-normal cursor-pointer justify-end"
              >
                Continue to {serviceType === 'emandate' ? 'e-mandate setup' : serviceName}
              </button>
            ) : null}
          </div>
          {serviceType === 'emandate' ? <SecuredByRazorpay /> : ''}
        </div>
      </Modal.Body>
    </Modal>
  );
};

export default OpenPopupLoader;
